import {TypesCore, TypesFlow, IEventEmitter} from "@dp-instant/interfaces";
import {Localize} from "@dp-instant/i18n";
import {Action, lazyGet, WaitAction} from "@dp-instant/core";
import {UIEvents, useInjection, Button} from "@dp-instant/ui";
import {GameModel} from "@game/coregame/model/GameModel";
import {TypesGame} from "@game/TypesGame";
import {CoinsIndicator} from "@game/ui/components/CoinsIndicator/CoinsIndicator";
import {PopupWinIndicator} from "@game/ui/components/CoinsIndicator/PopupWinIndicator";
import {ContinueButton} from "@game/ui/components/ContinueButton/ContinueButton";
import {UIConstants} from "@game/ui/constants";
import cn from "classnames";
import {useEffect, useState} from "preact/hooks";
import {SVG} from "../images";
import {playUIClickSound} from "@game/helpers";
import {hapticMedium} from "@game/haptic";
import "./LosePopup.less";

type LosePopupProps = {
    coins?: number;
    animationEnabled?: boolean;
};

export const LosePopup = ({animationEnabled = true, coins = 999}: LosePopupProps) => {
    const model = useInjection<GameModel>(TypesGame.model);
    const dispatcher: IEventEmitter = useInjection(TypesCore.dispatcher);

    const initialState = {
        startCoinsTotal: model.cookie.coins,
        reward: coins,
        visible: false,
        continueUsed: false,
    };

    const indicatorUpdateDelay = UIConstants.coinsIndicator.updateDelay;

    const [state, setState] = useState(() => initialState);
    useEffect(() => {
        const visibleTimeout = setTimeout(
            () => setState(state => ({...state, visible: true})),
            UIConstants.popup.startDelay
        );

        return () => {
            clearTimeout(visibleTimeout);
        };
    }, []);

    const close = async () => {
        updateCoinsModel(state.reward);
        await WaitAction.ms(indicatorUpdateDelay);
        dispatcher.emit(UIEvents.POPUP, {id: null});
    };

    const onContinue = async () => {
        await close();
        lazyGet<Action>(TypesFlow.LevelNext)?.run();
    };

    const onUpgrade = async () => {
        playUIClickSound();
        hapticMedium();
        await close();
        lazyGet<Action>(TypesFlow.mainAction)?.run();
    };

    const updateCoinsModel = (coins: number) => {
        model.cookie.coins += coins;
    };

    return (
        <div className="popups">
            <div className={cn("coins-bar")}>
                <CoinsIndicator
                    className={cn("coins-indicator", "coins-indicator_filled")}
                    total={state.startCoinsTotal}
                />
            </div>

            <div className="popup-lose">
                <div className={cn("popup", {visible: state.visible})}>
                    <div className="popup__title">
                        {Localize.get("ui-lose-lose_label", "YOU LOSE")}
                    </div>
                    {animationEnabled ? (
                        <div className="popup__main-animation">
                            <SVG.PopupLose />
                        </div>
                    ) : null}
                    <div className="popup__body">
                        <PopupWinIndicator
                            className={cn("coins-indicator", "coins-indicator_internal")}
                            total={state.reward}
                        />
                    </div>
                    <Button
                        className={cn("continue-button")}
                        onClick={() => { playUIClickSound(); hapticMedium(); onContinue(); }}
                    >
                        {Localize.get("ui-lose-try-again", "TRY AGAIN")}
                    </Button>
                    <Button
                        className={cn("continue-button", "continue-button--orange")}
                        onClick={onUpgrade}
                    >
                        {Localize.get("ui-lose-upgrade", "UPGRADE")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
